import React, { useEffect, useState } from "react";
import { api, formatMXN } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Edit, Plus, X } from "lucide-react";

const empty = {
  name: "", description: "", price: 0, compare_price: null,
  category_slug: "ropa", images: [""], stock: 0, tags: [],
  featured: false, sizes: [], colors: [],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/products?limit=500");
    setProducts(data);
  };

  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const save = async () => {
    try {
      const body = {
        ...editing,
        price: parseFloat(editing.price),
        compare_price: editing.compare_price ? parseFloat(editing.compare_price) : null,
        stock: parseInt(editing.stock || 0, 10),
        images: (editing.images || []).filter(Boolean),
        tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : editing.tags,
        sizes: typeof editing.sizes === "string" ? editing.sizes.split(",").map((s) => s.trim()).filter(Boolean) : editing.sizes,
        colors: typeof editing.colors === "string" ? editing.colors.split(",").map((s) => s.trim()).filter(Boolean) : editing.colors,
      };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Guardado");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Eliminado"); load();
  };

  const startNew = () => { setEditing({ ...empty }); setOpen(true); };
  const startEdit = (p) => {
    setEditing({
      ...p,
      tags: (p.tags || []).join(", "),
      sizes: (p.sizes || []).join(", "),
      colors: (p.colors || []).join(", "),
    });
    setOpen(true);
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-5xl tracking-tighter">Productos</h1>
          <p className="text-[#5B605E] mt-1">{products.length} productos en catálogo.</p>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="new-product-btn"><Plus size={16}/> Nuevo</button>
      </div>

      <div className="bg-white border border-[#E8E3DA]">
        <table className="w-full text-sm">
          <thead className="bg-[#F2EFE9] text-left text-[10px] uppercase tracking-wider text-[#5B605E]">
            <tr>
              <th className="px-4 py-3">Producto</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Precio</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Destacado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[#E8E3DA]" data-testid={`row-${p.slug}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={p.images?.[0]} alt="" className="h-12 w-10 object-cover" />
                  <span>{p.name}</span>
                </td>
                <td className="px-4 py-3 capitalize">{p.category_slug}</td>
                <td className="px-4 py-3">{formatMXN(p.price)}</td>
                <td className="px-4 py-3">{p.stock}</td>
                <td className="px-4 py-3">{p.featured ? "Sí" : "—"}</td>
                <td className="px-4 py-3 flex gap-2 justify-end">
                  <button onClick={() => startEdit(p)} className="p-2 hover:text-[#A94438]" data-testid={`edit-${p.slug}`}><Edit size={14}/></button>
                  <button onClick={() => remove(p)} className="p-2 hover:text-[#9E2A2B]" data-testid={`del-${p.slug}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-[#F9F6F0] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" data-testid="product-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl tracking-tighter">{editing.id ? "Editar producto" : "Nuevo producto"}</h2>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input className="col-span-2 border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Nombre" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="pf-name" />
              <textarea className="col-span-2 border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Descripción" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <select className="border border-[#2C302E] bg-transparent px-3 py-2" value={editing.category_slug} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })} data-testid="pf-cat">
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <input type="number" className="border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Precio" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="pf-price" />
              <input type="number" className="border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Precio antes (opcional)" value={editing.compare_price || ""} onChange={(e) => setEditing({ ...editing, compare_price: e.target.value })} />
              <input type="number" className="border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              <input className="col-span-2 border border-[#2C302E] bg-transparent px-3 py-2" placeholder="URL de imagen" value={editing.images[0] || ""} onChange={(e) => setEditing({ ...editing, images: [e.target.value] })} />
              <input className="border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Tallas (coma)" value={editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} />
              <input className="border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Colores (coma)" value={editing.colors} onChange={(e) => setEditing({ ...editing, colors: e.target.value })} />
              <input className="col-span-2 border border-[#2C302E] bg-transparent px-3 py-2" placeholder="Tags (coma)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              <label className="flex items-center gap-2 col-span-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Destacado</label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
              <button onClick={save} className="btn-primary" data-testid="pf-save">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
